import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, FormControl, InputLabel, Select, MenuItem, Typography, Box, IconButton, Alert, CircularProgress, Switch, FormControlLabel, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';

import { Close } from '@mui/icons-material';
import { CreateInventoryItemData } from '@services/inventoryService';

interface InventoryDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateInventoryItemData) => Promise<any>;
  editingItem?: any;
}

interface FormData {
  name: string;
  code: string;
  description: string;
  category: string;
  type: 'consumable' | 'equipment' | 'medication' | 'supply';
  unit: string;
  minStock: number;
  maxStock: number;
  cost: number;
  sellingPrice: number;
  supplier: string;
  location: string;
  autoConsumption: boolean;
  fifoEnabled: boolean;
  fefoEnabled: boolean;
  temperatureControlled: boolean;
  storageTemperature: string;
  serialized: boolean;
}

const InventoryDialog: React.FC<InventoryDialogProps> = ({
  open,
  onClose,
  onSave,
  editingItem
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    code: '',
    description: '',
    category: '',
    type: 'consumable',
    unit: 'buc',
    minStock: 0,
    maxStock: 100,
    cost: 0,
    sellingPrice: 0,
    supplier: '',
    location: '',
    autoConsumption: false,
    fifoEnabled: false,
    fefoEnabled: false,
    temperatureControlled: false,
    storageTemperature: '',
    serialized: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const categories = [
    'Medicamente',
    'Echipamente',
    'Consumabile',
    'Instrumente',
    'Produse de curatenie',
    'Hrana',
    'Accesorii',
    'Altele'
  ];

  const units = [
    'buc',
    'kg',
    'g',
    'l',
    'ml',
    'm',
    'cm',
    'pachet',
    'cutie',
    'flacon',
    'comprimat',
    'capsula',
    'ampula',
    'vial'
  ];

  const locations = [
    'Depozit principal',
    'Depozit secundar',
    'Cabinet 1',
    'Cabinet 2',
    'Sala de operatii',
    'Laborator',
    'Frigider',
    'Congelator',
    'Raft A',
    'Raft B',
    'Raft C'
  ];

  useEffect(() => {
    if (open) {
      if (editingItem) {
        setFormData({
          name: editingItem.name || '',
          code: editingItem.code || '',
          description: editingItem.description || '',
          category: editingItem.category || '',
          type: editingItem.type || 'consumable',
          unit: editingItem.unit || 'buc',
          minStock: editingItem.minStock || 0,
          maxStock: editingItem.maxStock || 100,
          cost: editingItem.cost || 0,
          sellingPrice: editingItem.sellingPrice || 0,
          supplier: editingItem.supplier || '',
          location: editingItem.location || '',
          autoConsumption: editingItem.autoConsumption || false,
          fifoEnabled: editingItem.fifoEnabled || false,
          fefoEnabled: editingItem.fefoEnabled || false,
          temperatureControlled: editingItem.temperatureControlled || false,
          storageTemperature: editingItem.storageTemperature || '',
          serialized: editingItem.serialized || false
        });
      } else {
        resetForm();
      }
    }
  }, [open, editingItem]);

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      category: '',
      type: 'consumable',
      unit: 'buc',
      minStock: 0,
      maxStock: 100,
      cost: 0,
      sellingPrice: 0,
      supplier: '',
      location: '',
      autoConsumption: false,
      fifoEnabled: false,
      fefoEnabled: false,
      temperatureControlled: false,
      storageTemperature: '',
      serialized: false
    });
    setError(null);
    setFieldErrors({});
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSwitchChange = (field: keyof FormData, checked: boolean) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Numele produsului este obligatoriu';
    }

    if (!formData.code.trim()) {
      errors.code = 'Codul produsului este obligatoriu';
    }

    if (!formData.category.trim()) {
      errors.category = 'Categoria este obligatorie';
    }

    if (!formData.unit.trim()) {
      errors.unit = 'Unitatea de masura este obligatorie';
    }

    if (formData.minStock < 0) {
      errors.minStock = 'Stocul minim nu poate fi negativ';
    }

    if (formData.maxStock < 0) {
      errors.maxStock = 'Stocul maxim nu poate fi negativ';
    }

    if (formData.minStock > formData.maxStock) {
      errors.maxStock = 'Stocul maxim trebuie sa fie mai mare decat stocul minim';
    }

    if (formData.cost < 0) {
      errors.cost = 'Costul nu poate fi negativ';
    }

    if (formData.sellingPrice < 0) {
      errors.sellingPrice = 'Pretul de vanzare nu poate fi negativ';
    }

    if (!formData.supplier.trim()) {
      errors.supplier = 'Furnizorul este obligatoriu';
    }

    if (!formData.location.trim()) {
      errors.location = 'Locatia este obligatorie';
    }

    if (formData.temperatureControlled && !formData.storageTemperature.trim()) {
      errors.storageTemperature = 'Temperatura de depozitare este obligatorie pentru produsele cu control de temperatura';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const inventoryData: CreateInventoryItemData = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        description: formData.description.trim(),
        category: formData.category.trim(),
        type: formData.type,
        unit: formData.unit.trim(),
        minStock: formData.minStock,
        maxStock: formData.maxStock,
        cost: formData.cost,
        sellingPrice: formData.sellingPrice,
        supplier: formData.supplier.trim(),
        location: formData.location.trim(),
        autoConsumption: formData.autoConsumption,
        fifoEnabled: formData.fifoEnabled,
        fefoEnabled: formData.fefoEnabled,
        temperatureControlled: formData.temperatureControlled,
        storageTemperature: formData.temperatureControlled ? formData.storageTemperature.trim() : undefined,
        serialized: formData.serialized,
        clinicId: '' // Will be set by the service
      };

      await onSave(inventoryData);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'A aparut o eroare la salvarea produsului');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {editingItem ? 'Editeaza Produs' : 'Produs Nou'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid size={12}>
            <Typography variant="h6" gutterBottom>
              Informatii de baza
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Nume produs *"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Cod produs *"
              value={formData.code}
              onChange={(e) => handleInputChange('code', e.target.value)}
              error={!!fieldErrors.code}
              helperText={fieldErrors.code}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label="Descriere"
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth error={!!fieldErrors.category}>
              <InputLabel>Categorie *</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                label="Categorie *"
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Tip</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                label="Tip"
              >
                <MenuItem value="consumable">Consumabil</MenuItem>
                <MenuItem value="equipment">Echipament</MenuItem>
                <MenuItem value="medication">Medicament</MenuItem>
                <MenuItem value="supply">Provizii</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth error={!!fieldErrors.unit}>
              <InputLabel>Unitate de masura *</InputLabel>
              <Select
                value={formData.unit}
                onChange={(e) => handleInputChange('unit', e.target.value)}
                label="Unitate de masura *"
              >
                {units.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Stock Information */}
          <Grid size={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Informatii stoc
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Stoc minim *"
              type="number"
              value={formData.minStock}
              onChange={(e) => handleInputChange('minStock', Number(e.target.value))}
              error={!!fieldErrors.minStock}
              helperText={fieldErrors.minStock}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Stoc maxim *"
              type="number"
              value={formData.maxStock}
              onChange={(e) => handleInputChange('maxStock', Number(e.target.value))}
              error={!!fieldErrors.maxStock}
              helperText={fieldErrors.maxStock}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Stoc curent"
              type="number"
              value={0}
              InputProps={{ readOnly: true }}
              helperText="Se va seta la primul intrare in stoc"
            />
          </Grid>

          {/* Pricing */}
          <Grid size={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Preturi
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Cost de achizitie *"
              type="number"
              value={formData.cost}
              onChange={(e) => handleInputChange('cost', Number(e.target.value))}
              error={!!fieldErrors.cost}
              helperText={fieldErrors.cost}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Pret de vanzare *"
              type="number"
              value={formData.sellingPrice}
              onChange={(e) => handleInputChange('sellingPrice', Number(e.target.value))}
              error={!!fieldErrors.sellingPrice}
              helperText={fieldErrors.sellingPrice}
            />
          </Grid>

          {/* Supplier and Location */}
          <Grid size={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Furnizor si locatie
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Furnizor *"
              value={formData.supplier}
              onChange={(e) => handleInputChange('supplier', e.target.value)}
              error={!!fieldErrors.supplier}
              helperText={fieldErrors.supplier}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth error={!!fieldErrors.location}>
              <InputLabel>Locatie *</InputLabel>
              <Select
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                label="Locatie *"
              >
                {locations.map((location) => (
                  <MenuItem key={location} value={location}>
                    {location}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Advanced Settings */}
          <Grid size={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Setari avansate
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.autoConsumption}
                  onChange={(e) => handleSwitchChange('autoConsumption', e.target.checked)}
                />
              }
              label="Consumare automata"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.fifoEnabled}
                  onChange={(e) => handleSwitchChange('fifoEnabled', e.target.checked)}
                />
              }
              label="FIFO (First In, First Out)"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.fefoEnabled}
                  onChange={(e) => handleSwitchChange('fefoEnabled', e.target.checked)}
                />
              }
              label="FEFO (First Expired, First Out)"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.serialized}
                  onChange={(e) => handleSwitchChange('serialized', e.target.checked)}
                />
              }
              label="Produs serializat"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.temperatureControlled}
                  onChange={(e) => handleSwitchChange('temperatureControlled', e.target.checked)}
                />
              }
              label="Control de temperatura"
            />
          </Grid>

          {formData.temperatureControlled && (
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Temperatura de depozitare *"
                value={formData.storageTemperature}
                onChange={(e) => handleInputChange('storageTemperature', e.target.value)}
                error={!!fieldErrors.storageTemperature}
                helperText={fieldErrors.storageTemperature || "Ex: 2-8°C, -20°C, temperatura camerei"}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Anuleaza
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Se salveaza...' : 'Salveaza'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryDialog;
